<script lang="ts" setup>
import '../../../../css/choice.css';
    import {onMounted,watch,reactive,computed} from 'vue';
    import {popAlert,ModalStyles} from '../../../../src/lib/Notification';
    import {ErrorReturnMessage,getClaimProcedure,getClaimPayments,getClaimProcedureComments} from '../../../../src/lib/Api/Claim';
    import {ClaimDisplay,IClaimTableFormat,ClaimTableWidth,ClaimColumnWidth,ClaimColumnDefinitions,amountClass,IClaimProcedureIds,IClaimProcedure,IClaimPayment,IClaimComment} from '../../../../src/lib/Models/ClaimModel';
    import {prettyCurrency} from '../../../../src/lib/Formatting/Number'; 
    import {initPinia} from '../../../lib/PiniaMgr';
    import {useRefundFullfillmentStore} from '../../../lib/Stores/RefundFullfillmentStore'; 
 
    const refundFullfillmentStore = useRefundFullfillmentStore(initPinia()); 
    import FloatInput from "../../../components/FloatInput.vue"; 
    
    const formData = reactive <
    {   
        claimTableFormat: IClaimTableFormat,
        procedurePayments?: IClaimPayment[],
        procedureComments? : IClaimComment[],
        procedureDetails?: IClaimProcedure, 
        commentColumnCount: number,
        display: {Payments: boolean, Comments: boolean},
        procedureTooltip: string
    }>({    
        claimTableFormat: {inputDisplay: false,inputLabel: "",levelDisplay: true, statusDisplay: false, payerDisplay: true, columns: 8, tableWidth:  (ClaimTableWidth + ClaimColumnDefinitions.Payer)},
        commentColumnCount: 5,
        display: {Payments: false, Comments: false},
        procedureTooltip: ""
    });
    const props = withDefaults(defineProps<{ 
        saveEnabled?: number,
        procedureIds? : IClaimProcedureIds, 
        display: ClaimDisplay,
        claimTableFormat: IClaimTableFormat,
        displayPayments: boolean,
        displayComments: boolean 
    }>(),
    {
        saveEnabled: 0,  
        headerColumns: 0,        
        displayPayments: false,
        displayComments: false 
    });

    onMounted(async () => { 
        formData.claimTableFormat = props.claimTableFormat;
        formData.claimTableFormat.tableWidth -= ClaimColumnWidth; 
        formData.commentColumnCount += (formData.claimTableFormat.payerDisplay ? 1 : 0) + (formData.claimTableFormat.inputDisplay ? 1 : 0); 
        formData.display.Payments = props.displayPayments;
        formData.display.Comments = props.displayComments;
        refreshData();
    });  

    watch(() => props.displayPayments, (newPayments : boolean,oldPayments : boolean) => { 
        if (newPayments !== oldPayments) { 
            if (newPayments) {
                loadPayments();   
            }
            else {
                formData.procedurePayments = [];
            }        
        }
    });
    watch(() => props.displayComments, (newComments : boolean,oldComments : boolean) => { 
        if (newComments !== oldComments) { 
            if (newComments) {
                loadComments();   
            }
            else {
                formData.procedureComments = [];
            }        
        }
    });
    
    const showProcedureLine = computed<boolean>(()=> { 
        if ((props.display != ClaimDisplay.Transfer) || ((formData.procedureDetails?.balance ?? 0) > 0))
        { 
            return true;
        }
        return false;
    });

    async function refreshData() : Promise<void>  {
        if (!props.procedureIds) {  
            return;
        }
        const detailsGet = await getClaimProcedure(props.procedureIds.claimID,props.procedureIds.diagnosisID);
        if (!detailsGet.success) {  
            await popAlert((detailsGet.error?.error ?? ErrorReturnMessage.procedure),"Procedure Details",ModalStyles.danger);
            return;
        }
        formData.procedureDetails = detailsGet.success;     
        formData.procedureDetails.balance = parseFloat((formData.procedureDetails.amount + formData.procedureDetails.payments + formData.procedureDetails.adjustments).toFixed(2));
        formData.procedureTooltip = `Line #: ${formData.procedureDetails.diagnosisLine}\r\nProcedure Code: ${formData.procedureDetails.procedureCode}\r\Description: ${formData.procedureDetails.procedureDescription}\r\nModifiers: ${formData.procedureDetails.modifiers}`;
        if (formData.display.Payments) {
            loadPayments();
        }
        if (formData.display.Comments) {
            loadComments();
        }
        if (props.display === ClaimDisplay.Transfer) {
            refundFullfillmentStore.loadProcedureTransfers(formData.procedureDetails,showProcedureLine.value);
        }
    } 
    async function loadPayments() : Promise<void> {
        if (!props.procedureIds) {  
            return;
        }
        const paymentGet = await getClaimPayments(props.procedureIds.claimID,props.procedureIds.diagnosisID);
        if (!paymentGet.success) {  
            await popAlert((paymentGet.error?.error ?? ErrorReturnMessage.payments),"Procedure Payments",ModalStyles.danger);
            return;
        }
        formData.procedurePayments = paymentGet.success; 
    }
    async function loadComments() : Promise<void> {
        if (!props.procedureIds) {  
            return;
        }
        const commentsGet = await getClaimProcedureComments(props.procedureIds.claimID,props.procedureIds.diagnosisID);
        if (!commentsGet.success) {  
            await popAlert((commentsGet.error?.error ?? ErrorReturnMessage.procedureComments),"Procedure Comments",ModalStyles.danger);
            return;
        }
        formData.procedureComments = commentsGet.success;
    }
    function procedureUpdate() : void { 
        refundFullfillmentStore.setTransferUpdatedBalances();
    }
</script> 
 
<style scoped> 
.viewDisplay {
    display: block;
}
.viewHide {
    display: none;
}
.cardActions {
    font-size: .85rem;
    font-weight: 500;
    color: #204D89;
}
.billing-header {
  color: #204D89;
  background-color: #f1f1f1;
}
.billing-data-left {
  color: black;
  background-color: white;
  text-align: left;
}    
.billing-data-center {
  color: black;
  background-color: white;
  text-align: center;
}
.billing-data-right {
  color: black;
  background-color: white;
  text-align: right;
} 
.billing-text { 
  FONT-SIZE: 8pt;
  font-family: Arial, sans-serif, Helvetica;
  padding: 5px 5px 5px 5px;
  cursor: default;
  vertical-align: middle;
}
.billing-reversal { 
    text-decoration: line-through; 
}
.billing-break {
    border-top: 1px solid silver;
}
</style>
<template>
    <table v-if="(showProcedureLine && formData.procedureDetails)" border="0" aria-hidden="true" :style="`table-layout: fixed; border-collapse: collapse;  width: ${formData.claimTableFormat.tableWidth}rem;`">
        <colgroup>
            <col :style="`width: ${ClaimColumnDefinitions.ClaimID - ClaimColumnWidth}rem;`"/>
            <col :style="`width: ${ClaimColumnDefinitions.DOS}rem;`"/>
            <col v-if="formData.claimTableFormat.levelDisplay" :style="`width: ${ClaimColumnDefinitions.Level}rem;`"/>
            <col v-if="formData.claimTableFormat.statusDisplay" :style="`width: ${ClaimColumnDefinitions.Status}rem;`"/>
            <col v-if="formData.claimTableFormat.payerDisplay" :style="`width: ${ClaimColumnDefinitions.Payer}rem;`"/> 
            <col :style="`width: ${ClaimColumnDefinitions.Charges}rem;`"/>
            <col :style="`width: ${ClaimColumnDefinitions.Payments}rem;`"/>
            <col :style="`width: ${ClaimColumnDefinitions.Adjustments}rem;`"/>
            <col :style="`width: ${ClaimColumnDefinitions.Balance}rem;`"/>
            <col v-if="formData.claimTableFormat.inputDisplay" :style="`width: ${ClaimColumnDefinitions.Input}rem;`"/>
        </colgroup>
        <tr class="billing-text">
            <td class="text-end" :title="formData.procedureTooltip">
                {{formData.procedureDetails.procedureCode ?? ""}}
            </td>
            <td class="text-center" :title="`From: ${formData.procedureDetails.fromDate} - ${formData.procedureDetails.toDate}`">
                {{formData.procedureDetails.fromDate ?? ""}}
            </td>
            <td v-if="formData.claimTableFormat.levelDisplay">&nbsp;</td>
            <td v-if="formData.claimTableFormat.statusDisplay">&nbsp;</td>
            <td v-if="formData.claimTableFormat.payerDisplay">&nbsp;</td> 
            <td :class="amountClass(formData.procedureDetails.amount,0,false)"
                :title="`Amount: ${prettyCurrency(formData.procedureDetails.amount)}\r\nUnits: ${formData.procedureDetails.units}\r\Unit Charge: ${prettyCurrency(formData.procedureDetails?.unitCharge)}`">
                {{prettyCurrency((formData.procedureDetails.amount ?? 0),{negativeCurrencyDisplay: true})}}
            </td>
            <td :class="amountClass(formData.procedureDetails.payments,0,false)"
                :title="`Payments: ${prettyCurrency(formData.procedureDetails.payments)}`">
                {{prettyCurrency((formData.procedureDetails.payments ?? 0),{negativeCurrencyDisplay: true})}}
            </td>
            <td :class="amountClass(formData.procedureDetails?.adjustments,0,false)"
                :title="`Adjustments: ${prettyCurrency(formData.procedureDetails.adjustments)}`">
                {{prettyCurrency((formData.procedureDetails.adjustments ?? 0),{negativeCurrencyDisplay: true})}}
            </td>
            <td :class="amountClass(formData.procedureDetails.balance,0,false)"
                :title="`Balance: ${prettyCurrency(formData.procedureDetails.balance)}`">
                {{prettyCurrency((formData.procedureDetails.balance),{negativeCurrencyDisplay: true})}}
            </td>
            <td v-if="formData.claimTableFormat.inputDisplay">
                <span v-if="props.display == ClaimDisplay.Transfer">
                    <FloatInput 
                        v-model:numberValue="formData.procedureDetails.transferAmount"
                        :maxLength="10"
                        :startingRange="0"
                        :endingRange="formData.procedureDetails.balance"
                        :minPrecision="2"
                        :maxPrecision="2"
                        :positive="true"
                        :errorDisplay="false"
                        :cssFramework="'vanilla'"
                        :placeholder="'transfer'"
                        @update:numberValue="procedureUpdate"
                    />
                </span>
                <span v-if="props.display != ClaimDisplay.Transfer">
                    &nbsp;
                </span>
            </td>
        </tr> 
        <tr class="text-muted billing-text" v-if="(formData.procedurePayments?.length ?? 0) > 0" v-for="(paymentRecord,index) in formData.procedurePayments" :key="`PMT${paymentRecord.paymentID}`">
            <td>&nbsp;</td>
            <td :title="`Deposit Date: ${paymentRecord.paymentDate}\r\nPost Date: ${paymentRecord.postDate}\r\nPaid By: ${paymentRecord.payerName}\r\nCheck/Trace #: ${paymentRecord.checkNumber}\r\nReversal: ${(paymentRecord.paymentReversed === 1 ? 'Yes' : 'No')}`">
                {{paymentRecord.paymentDate}}
            </td>
            <td :title="`Method: ${paymentRecord.paymentMethodDescription}\r\nType: ${paymentRecord.paymentTypeDescription}${(paymentRecord.commentIndicator == 'Y' ? '\r\nComment: Yes' : '')}`">
                {{paymentRecord.paymentMethodDescription}}
            </td>
            <td v-if="formData.claimTableFormat.payerDisplay" class="text-truncate" :title="`Paid By: ${paymentRecord.payerName}`">
                <div :style="`width: ${ClaimColumnDefinitions.Input}rem;`">{{paymentRecord.payerName}}</div>
            </td>
            <td>&nbsp;</td>
            <td :class="amountClass(paymentRecord.paymentAmount,paymentRecord.paymentReversed,true)" 
                :title="`${(paymentRecord.paymentAmount != 0 && paymentRecord.commentIndicator == 'Y' ? 'Comment ' : '')}Payment: ${prettyCurrency(paymentRecord.paymentAmount)}`">
                {{(paymentRecord.paymentAmount != 0 && paymentRecord.commentIndicator == "Y" ? "**" : "")}}
                {{prettyCurrency((paymentRecord.paymentAmount),{negativeCurrencyDisplay: true})}}
            </td>
            <td :class="amountClass(paymentRecord.adjustmentAmount,paymentRecord.paymentReversed,true)" 
                :title="`${(paymentRecord.adjustmentAmount != 0 && paymentRecord.commentIndicator == 'Y' ? 'Comment ' : '')}Adjustment: ${prettyCurrency(paymentRecord.adjustmentAmount)}`">
                {{(paymentRecord.adjustmentAmount != 0 && paymentRecord.commentIndicator == "Y" ? "**" : "")}}
                {{prettyCurrency((paymentRecord.adjustmentAmount),{negativeCurrencyDisplay: true})}}
            </td>
            <td>&nbsp;</td>
            <td v-if="formData.claimTableFormat.inputDisplay">&nbsp;</td>
        </tr>
        <tr class="text-muted fst-italic billing-text" v-if="(formData.procedureComments?.length ?? 0) > 0" v-for="(procedureCommentRecord,index) in formData.procedureComments" :key="`CMT${procedureCommentRecord.commentID}`">
            <td>&nbsp;</td>
            <td :class="(index === 0 ? 'billing-break' : '')">{{procedureCommentRecord.enterDate}}</td>
            <td :class="(index === 0 ? 'billing-break' : '')">{{procedureCommentRecord.userID}}</td>
            <td :class="(index === 0 ? 'billing-break' : '')" :colspan="formData.commentColumnCount">{{procedureCommentRecord.comment}}</td> 
        </tr>
    </table> 
</template>
